<script lang="ts">
  import { Link } from "@inertiajs/svelte";
  import type { Trip } from "../../lib/types";

  let {
    trip,
    currentPage = "overview",
  }: {
    trip: Trip & { isPublic?: boolean };
    currentPage?: string;
  } = $props();

  const statusColors: Record<string, { bg: string; color: string }> = {
    planning:  { bg: "color-mix(in srgb, var(--surface-hover) 92%, transparent)",  color: "var(--text-secondary)" },
    active:    { bg: "rgba(16,185,129,0.15)",   color: "#34d399" },
    completed: { bg: "rgba(32,178,170,0.15)",   color: "#20b2aa" },
    cancelled: { bg: "rgba(239,68,68,0.12)",    color: "#f87171" },
  };

  const statusLabels: Record<string, string> = {
    planning: "계획중",
    active: "진행중",
    completed: "완료",
    cancelled: "취소됨",
  };

  // SVG path data (Heroicons outline 24px)
  const navIcons: Record<string, string> = {
    overview:       `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />`,
    map:            `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />`,
    schedule:       `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />`,
    accommodations: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />`,
    flights:        `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />`,
    expenses:       `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />`,
    checklist:      `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />`,
    wishlist:       `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />`,
    finance:        `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />`,
    story:          `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />`,
    transport:      `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />`,
    shopping:       `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />`,
    localTransport: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /><circle cx="8" cy="16" r="1" fill="currentColor" /><circle cx="16" cy="16" r="1" fill="currentColor" />`,
    album:           `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />`,
    plan:            `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />`,
    settlement:      `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />`,
    recommendations: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />`,
    companions:      `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />`,
  };

  let subNavItems = $derived([
    { key: "overview",       href: `/trips/${trip.id}`,              label: "개요" },
    { key: "map",            href: `/trips/${trip.id}/map`,          label: "지도" },
    { key: "schedule",       href: `/trips/${trip.id}/schedule`,     label: "일정" },
    { key: "accommodations", href: `/trips/${trip.id}/accommodations`, label: "숙소" },
    { key: "flights",        href: `/trips/${trip.id}/flights`,      label: "항공" },
    { key: "transport",      href: `/trips/${trip.id}/transport`,    label: "이동" },
    { key: "expenses",       href: `/trips/${trip.id}/expenses`,     label: "지출" },
    { key: "shopping",       href: `/trips/${trip.id}/shopping`,     label: "쇼핑" },
    { key: "localTransport", href: `/trips/${trip.id}/local-transport`, label: "교통" },
    { key: "checklist",      href: `/trips/${trip.id}/checklist`,    label: "준비물" },
    { key: "wishlist",       href: `/trips/${trip.id}/wishlist`,     label: "위시" },
    { key: "album",          href: `/trips/${trip.id}/album`,        label: "앨범" },
    { key: "finance",        href: `/trips/${trip.id}/finance`,      label: "재무" },
    { key: "story",          href: `/trips/${trip.id}/story`,        label: "스토리" },
    { key: "plan",            href: `/trips/${trip.id}/plan`,             label: "PC플래너" },
    { key: "settlement",      href: `/trips/${trip.id}/settlement`,       label: "정산" },
    { key: "recommendations", href: `/trips/${trip.id}/recommendations`,  label: "AI추천" },
    { key: "companions",      href: `/trips/${trip.id}/companions`,       label: "동행자" },
  ]);

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function getTripDaysInfo(): string {
    const now = new Date();
    const start = new Date(trip.startDate);
    const end = new Date(trip.endDate);

    if (now < start) {
      const days = Math.ceil((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return `D-${days}`;
    } else if (now >= start && now <= end) {
      const days = Math.ceil((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      return `${days}일차`;
    } else {
      return "종료";
    }
  }

  function setBackLinkColor(event: MouseEvent, color: string) {
    (event.currentTarget as HTMLElement).style.color = color;
  }
</script>

<header class="sticky top-16 z-30 trip-subnav-shell">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Trip Info Bar -->
    <div class="py-3 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <Link href="/trips" class="p-1 rounded-lg transition-colors" style="color: var(--text-muted);" onmouseenter={(e: MouseEvent) => setBackLinkColor(e, 'var(--text-primary)')} onmouseleave={(e: MouseEvent) => setBackLinkColor(e, 'var(--text-muted)')}>
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div class="min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <h1 class="text-lg font-bold truncate" style="color: var(--text-primary);">{trip.name}</h1>
            <span class="px-2 py-0.5 rounded-full text-xs font-medium" style="background: {(statusColors[trip.status] ?? statusColors.planning).bg}; color: {(statusColors[trip.status] ?? statusColors.planning).color};">
              {statusLabels[trip.status] || "계획중"}
            </span>
            {#if trip.isPublic}
              <span class="px-2 py-0.5 rounded-full text-xs font-medium" style="background: rgba(16,185,129,0.15); color: #34d399;">공개</span>
            {/if}
          </div>
          <p class="text-xs truncate" style="color: var(--text-muted);">
            {trip.destination} • {formatDate(trip.startDate)} ~ {formatDate(trip.endDate)}
          </p>
        </div>
      </div>
      <span class="flex-shrink-0 px-3 py-1 rounded-full text-sm font-medium" style="background: color-mix(in srgb, var(--accent) 15%, transparent); color: var(--accent);">
        {getTripDaysInfo()}
      </span>
    </div>

    <!-- Sub Navigation -->
    <nav class="flex gap-1 overflow-x-auto pb-2 scrollbar-hide">
      {#each subNavItems as item}
        <Link
          href={item.href}
          class="subnav-item flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all {currentPage === item.key ? 'subnav-active' : ''}"
        >
          <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {@html navIcons[item.key] ?? ''}
          </svg>
          <span class="hidden sm:inline">{item.label}</span>
        </Link>
      {/each}
    </nav>
  </div>
</header>

<style>
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  :global(.subnav-item) {
    color: var(--text-secondary);
  }
  :global(.subnav-item:hover) {
    background: color-mix(in srgb, var(--surface-hover) 88%, transparent);
    color: var(--text-primary);
  }
  :global(.subnav-item.subnav-active) {
    background: color-mix(in srgb, var(--accent) 18%, transparent);
    color: var(--accent);
    box-shadow: 0 0 12px color-mix(in srgb, var(--accent) 20%, transparent);
  }

  .trip-subnav-shell {
    background: color-mix(in srgb, var(--surface) 88%, transparent);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border);
  }
</style>
